import { useQuery } from '@tanstack/react-query';

import { getI18nLocale } from '@front/i18n';

import { DetailsResponseItem, WoosmapType } from '../../types/commons';
import { Country } from '../../types/country';
import { getCurrentCountry } from '../../utils/country';

import { WithApiKey, WithPrivateKey } from './types';

export const getKey = (options: WithApiKey | WithPrivateKey): Record<string, string> => {
  if ('apiKey' in options) return { key: options.apiKey };
  // In mobile we have to use privateKey and not public key
  return { private_key: options.privateKey };
};

const extractLocale = () => {
  const locale = getI18nLocale();
  return locale.split('-')[0];
};

const buildHeaders = () => ({
  'Accept-Language': extractLocale(),
});

export const api = {
  geolocation: {
    get: (keys: WithApiKey | WithPrivateKey) =>
      fetch(
        `https://api.woosmap.com/geolocation/position?${new URLSearchParams({
          ...getKey(keys),
        })}`,
        {
          headers: buildHeaders(),
        },
      ),
  },
};

export type AutocompleteResponseItem = {
  public_id: string;
  description: string;
  type: WoosmapType;
  types: WoosmapType[];
};

// TODO I18N: What to use here for new countries?
const woosmapCountry: Record<Country, string> = {
  FR: 'country:fr-fr',
  GB: 'country:gb',
  DE: 'country:de',
  AT: 'country:at',
  CH: 'country:ch',
};

type UseAddressQueryParams = (WithApiKey | WithPrivateKey) & {
  keyword: string;
  location?: string;
};

type GeolocationResponse = {
  latitude: number;
  longitude: number;
};

type UseGeolocationParams = (WithApiKey | WithPrivateKey) & {
  enabled?: boolean;
};

export const useGeolocationQuery = ({ enabled = false, ...keys }: UseGeolocationParams) => {
  const { data: geolocation } = useQuery<GeolocationResponse, Error>({
    queryKey: ['geolocation', keys],
    queryFn: async () => {
      const data = await api.geolocation.get(keys);
      return data.json();
    },
    enabled,
  });
  return geolocation;
};

export const useAddressQuery = ({ keyword, location, ...keys }: UseAddressQueryParams) =>
  useQuery<AutocompleteResponseItem[], Error>({
    // To avoid flickering of suggestions in the autocomplete address
    keepPreviousData: !!keyword,
    queryKey: ['address', keyword, keys, location],
    queryFn: async ({ signal }) => {
      const data = await fetch(
        `https://api.woosmap.com/localities/autocomplete/?${new URLSearchParams({
          input: keyword,
          components: woosmapCountry[getCurrentCountry()],
          types: 'address',
          ...(location ? { location } : {}),
          ...getKey(keys),
        })}`,
        {
          signal,
          headers: buildHeaders(),
        },
      );
      const dataJson = await data.json();
      return dataJson.localities;
    },
    enabled: keyword.length > 4,
  });

type UseAddressDetailParams = (WithApiKey | WithPrivateKey) & {
  publicId?: string;
};
export const useAddressDetailQuery = ({ publicId, ...keys }: UseAddressDetailParams) => {
  const { data: addressDetail } = useQuery<DetailsResponseItem, Error>({
    queryKey: ['address_detail', publicId, keys],
    queryFn: async () => {
      const data = await fetch(
        `https://api.woosmap.com/localities/details?${new URLSearchParams({
          public_id: publicId || '',
          ...getKey(keys),
        })}`,
        {
          headers: buildHeaders(),
        },
      );
      const dataJson = await data.json();
      return dataJson.result;
    },
    enabled: !!publicId,
  });
  return { addressDetail };
};
